import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { InsurancePolicyProduct } from 'src/interfaces/insurancePolicy'

interface Props {
  isOpen: boolean
  policyProduct: InsurancePolicyProduct | null
  isDeleting: boolean
  onConfirm(): void
  onClose(): void
}

const DeleteInsurancePolicyProductModalComponent = ({
  isOpen,
  policyProduct,
  isDeleting,
  onConfirm,
  onClose,
}: Props) => {
  if (!policyProduct) {
    return null
  }

  const productName = policyProduct.insuranceProduct?.name ?? policyProduct.insuranceProductId

  return (
    <CommonModal
      isOpen={isOpen}
      title="Remove this product from the policy?"
      onClose={onClose}
      buttons={
        <Fragment>
          <Button size="small" color="grey" disabled={isDeleting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            color="red"
            loading={isDeleting}
            disabled={isDeleting}
            onClick={onConfirm}
            data-testid="insurance-policy-product-delete-confirm-button"
          >
            Remove product
          </Button>
        </Fragment>
      }
    >
      <div css={tw`flex flex-col gap-2`}>
        <Typography variant="body2">
          {productName} will no longer be part of this policy.
        </Typography>
        {/* The delete always sends operationType=immutable, so this is not a warning about a
            choice — it is what the action does, every time. */}
        <Typography variant="body2" css={tw`text-shades-500`}>
          Removing a product always locks any documents already collected for it. This cannot be
          undone.
        </Typography>
      </div>
    </CommonModal>
  )
}

export const DeleteInsurancePolicyProductModal = memo(DeleteInsurancePolicyProductModalComponent)
